import { QuestionCircleOutlined } from "@ant-design/icons";
import type { ProColumns } from "@ant-design/pro-table";
import { Button, Popconfirm, Spin } from "antd";
import AntdTablePro from "components/antdTablePro";
import { debounce } from "lodash";
import {
  useWorkerDelete,
  useWorkerListPaginated,
} from "hooks/queries/useWorker";
import { useWorkerImpersonate } from "hooks/queries/useWorkerImpersonate";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { BiPencil, BiPlus, BiShow, BiTrash, BiUser } from "react-icons/bi";
import { Link } from "react-router-dom";
import { WorkerSchema } from "types/worker";
import WorkerModal from "./WorkerModal";

type ModalState =
  | {
      isOpen: false;
      value: null;
    }
  | {
      isOpen: true;
      value: WorkerSchema;
    };

type FilterParams = {
  page: number;
  size: number;
  search: string;
};

function Index() {
  const [filterParams, setFilterParams] = useState<FilterParams>({
    page: 1,
    size: 100,
    search: "",
  });

  const [modalState, setModalState] = useState<ModalState>({
    isOpen: false,
    value: null,
  });

  const { isLoading, data } = useWorkerListPaginated(filterParams);

  const { t } = useTranslation();

  if (isLoading) return <Spin />;

  const columns: ProColumns[] = [
    {
      title: t("general:firstName"),
      dataIndex: "firstName",
    },
    {
      title: t("general:lastName"),
      dataIndex: "lastName",
    },
    {
      title: t("general:userName"),
      dataIndex: "userName",
    },
    {
      title: t("login:email"),
      dataIndex: "email",
    },
    {
      title: t("general:action"),
      render: (_, row: WorkerSchema) => (
        <div className="flex gap-2">
          <Button
            type="primary"
            onClick={() => {
              setModalState({
                isOpen: true,
                value: row,
              });
            }}
          >
            <BiShow />
          </Button>
          <ImpersonateButton worker={row} />
          <Link
            key="editLink"
            to={`/worker/edit/${row.id}`}
            className="ant-btn ant-btn-primary"
            style={{
              display: "flex",
              alignItems: "center",
              background: "#ffb630",
            }}
          >
            <BiPencil />
          </Link>
          <DeleteButton worker={row} />
        </div>
      ),
    },
  ];

  const getSearchKeywordsDeb = debounce((value) => {
    setFilterParams((prev) => ({ ...prev, page: 1, search: value }));
  }, 500);

  return (
    <React.Fragment>
      <AntdTablePro
        columns={columns}
        rowKey="id"
        dataSource={data?.items}
        className="order-table"
        pagination={{
          showSizeChanger: true,
          showQuickJumper: true,
          current: filterParams?.page,
          pageSize: filterParams?.size,
          total: data?.count,
          onChange: (page, pageSize) => {
            setFilterParams({
              ...filterParams,
              page: page,
              size: pageSize,
            });
          },
        }}
        toolBarRender={[
          <Link
            key="addLink"
            to="/worker/add"
            className="ant-btn ant-btn-primary header-add-link"
            style={{
              display: "flex",
              alignItems: "center",
            }}
          >
            <span className="icon">
              <BiPlus />
            </span>
            <span className="text">{t("worker:addWorker")}</span>
          </Link>,
        ]}
        inputElement={{
          placeholder: t("main:search"),
          handleChange: (e) => getSearchKeywordsDeb(e.target.value),
        }}
      />
      {modalState.isOpen && (
        <WorkerModal
          visible={modalState.isOpen}
          defaultValue={modalState.value}
          modalText={
            modalState.value.firstName + " " + modalState.value.lastName
          }
          onHide={() => setModalState({ isOpen: false, value: null })}
        />
      )}
    </React.Fragment>
  );
}

export default Index;

function DeleteButton({ worker }: { worker: WorkerSchema }) {
  const { t } = useTranslation();
  const { mutate, isLoading } = useWorkerDelete(worker.id);

  return (
    <Popconfirm
      placement="leftBottom"
      icon={<QuestionCircleOutlined style={{ color: "red" }} />}
      title={t("general:confirmDelete")}
      onConfirm={() => mutate()}
      okText={t("general:yes")}
      cancelText={t("general:no")}
    >
      <Button type="primary" style={{ background: "red" }} loading={isLoading}>
        <BiTrash />
      </Button>
    </Popconfirm>
  );
}

function ImpersonateButton({ worker }: { worker: WorkerSchema }) {
  const { t } = useTranslation();
  const { mutate, isLoading } = useWorkerImpersonate();

  const handleImpersonate = () => {
    mutate(worker.id);
  };

  return (
    <Button
      type="primary"
      style={{ background: "#52c41a" }}
      loading={isLoading}
      onClick={handleImpersonate}
      title={t("worker:impersonateWorker")}
    >
      <BiUser />
    </Button>
  );
}
